// Programmatic SEO data. Every entry here becomes a crawlable, ranking page.
//
// Two page families:
//   1. Service × City   → e.g. /services/mobile-apps-in-delhi
//   2. Service × Industry → e.g. /services/custom-ai-for-healthcare
//
// Rule: each page must have UNIQUE value — a unique intro, an industry/city-specific
// challenge + proof angle, and a distinct CTA. Don't ship template spam; Google
// penalises it post-2024.

export const SERVICE_SEO = {
  mobile: {
    slug: "mobile-app-development",
    title: "Mobile App Development",
    tagline: "Native-feel iOS, Android, and cross-platform apps",
    summary:
      "We build production mobile apps with React Native, Swift, and Kotlin — shipping pixel-perfect, offline-capable, payment-ready products used by thousands of people per day.",
    ogKeyword: "mobile app development",
  },
  "ai-websites": {
    slug: "ai-website-development",
    title: "AI-Powered Website Development",
    tagline: "Intelligent, personalised, ranking websites",
    summary:
      "Websites with semantic search, chat, generative content, and vector-store recommendations baked in — all served from edge CDNs for sub-200ms global performance.",
    ogKeyword: "AI website development",
  },
  "custom-ai": {
    slug: "custom-ai-development",
    title: "Custom AI Development",
    tagline: "Bespoke RAG, agents, and LLM copilots",
    summary:
      "We build private AI systems trained on your company's data — RAG pipelines, multi-agent workflows, fine-tuned models — deployed on your cloud or on-prem.",
    ogKeyword: "custom AI development",
  },
  software: {
    slug: "software-development",
    title: "Software Development",
    tagline: "From MVP to enterprise platform",
    summary:
      "Full-stack product engineering — web apps, APIs, desktop software — built on battle-tested stacks with clean architecture and teams that actually ship.",
    ogKeyword: "software development",
  },
  seo: {
    slug: "seo-engineering",
    title: "SEO Engineering",
    tagline: "Technical SEO + programmatic content + Core Web Vitals",
    summary:
      "Data-driven SEO for teams that want organic to be a primary channel. Technical audits, schema, programmatic SEO, and AI-assisted topic clusters that actually rank.",
    ogKeyword: "SEO engineering agency",
  },
  marketing: {
    slug: "performance-marketing",
    title: "Performance Marketing",
    tagline: "Full-funnel paid, lifecycle, and creative",
    summary:
      "Paid media strategy, creative production, lifecycle automation, and attribution — all run in-house by senior operators, not interns with a dashboard.",
    ogKeyword: "performance marketing agency",
  },
  saas: {
    slug: "saas-development",
    title: "SaaS Development",
    tagline: "Multi-tenant platforms with billing, auth, and admin",
    summary:
      "We build SaaS from zero: auth, Stripe billing, admin panel, multi-tenant isolation, dashboards. Production-grade launches in 6–10 weeks, not quarters.",
    ogKeyword: "SaaS development agency",
  },
};

export const CITIES = [
  {
    slug: "delhi",
    name: "Delhi",
    display: "Delhi",
    region: "NCR",
    country: "India",
    coordinates: "28.7041°N 77.1025°E",
    localProof:
      "We're headquartered in Delhi — every project is run by Delhi-based senior engineers, not outsourced.",
    localSignal: "in-person kickoff workshops at your office across Delhi NCR",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "gurugram",
    name: "Gurugram",
    display: "Gurugram (Gurgaon)",
    region: "NCR",
    country: "India",
    coordinates: "28.4595°N 77.0266°E",
    localProof:
      "A 30-minute drive from our Delhi office — we regularly run on-site discovery sessions with Cyber City and Golf Course Road teams.",
    localSignal: "same-day in-person discovery calls at your Gurugram office",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "noida",
    name: "Noida",
    display: "Noida",
    region: "NCR",
    country: "India",
    coordinates: "28.5355°N 77.3910°E",
    localProof:
      "20 minutes from Delhi — clients in Sector 62 and Film City regularly work with us on-site.",
    localSignal: "weekly on-site presence during discovery and launch sprints",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "bangalore",
    name: "Bangalore",
    display: "Bangalore (Bengaluru)",
    region: "Karnataka",
    country: "India",
    coordinates: "12.9716°N 77.5946°E",
    localProof:
      "We've shipped for Bangalore-based fintech, SaaS, and AI startups — async-first collaboration with monthly on-site visits.",
    localSignal: "monthly on-site sprints during active engagements",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "mumbai",
    name: "Mumbai",
    display: "Mumbai",
    region: "Maharashtra",
    country: "India",
    coordinates: "19.0760°N 72.8777°E",
    localProof:
      "Mumbai clients get the same async + on-demand on-site access — we've worked with Fort, BKC, and Powai teams.",
    localSignal: "BKC and Fort on-site visits during milestone reviews",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "hyderabad",
    name: "Hyderabad",
    display: "Hyderabad",
    region: "Telangana",
    country: "India",
    coordinates: "17.3850°N 78.4867°E",
    localProof:
      "Hyderabad startups in HITEC City and Gachibowli regularly partner with us — same timezone, overlapping working hours.",
    localSignal: "full working-hour overlap with HITEC City teams",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "pune",
    name: "Pune",
    display: "Pune",
    region: "Maharashtra",
    country: "India",
    coordinates: "18.5204°N 73.8567°E",
    localProof:
      "Pune's engineering-heavy ecosystem fits our craft-first working style — we've built for Hinjewadi and Baner teams.",
    localSignal: "monthly Hinjewadi / Baner on-site sprints",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "chennai",
    name: "Chennai",
    display: "Chennai",
    region: "Tamil Nadu",
    country: "India",
    coordinates: "13.0827°N 80.2707°E",
    localProof:
      "Chennai's OMR corridor startups regularly work with us — we serve 100%-remote with quarterly on-site.",
    localSignal: "quarterly OMR on-site visits for major milestones",
    timezone: "IST (UTC+5:30)",
  },
  {
    slug: "san-francisco",
    name: "San Francisco",
    display: "San Francisco",
    region: "California",
    country: "United States",
    coordinates: "37.7749°N 122.4194°W",
    localProof:
      "Our IST working hours cover 9pm–5am PST — we handle overnight handoffs for SF teams that ship at SF pace with Indian margins.",
    localSignal: "overnight async handoffs, 9am PST standups with India team",
    timezone: "PST / PDT",
  },
  {
    slug: "london",
    name: "London",
    display: "London",
    region: "England",
    country: "United Kingdom",
    coordinates: "51.5074°N 0.1278°W",
    localProof:
      "4.5-hour working overlap with London — morning standups London time, afternoon delivery from Delhi.",
    localSignal: "4.5-hour daily overlap window for London clients",
    timezone: "GMT / BST",
  },
  {
    slug: "singapore",
    name: "Singapore",
    display: "Singapore",
    region: "Asia-Pacific",
    country: "Singapore",
    coordinates: "1.3521°N 103.8198°E",
    localProof:
      "Only 2.5 hours ahead of Delhi — full-day working overlap with Singapore SaaS and fintech teams.",
    localSignal: "full working-hour overlap and quarterly on-site",
    timezone: "SGT (UTC+8)",
  },
  {
    slug: "dubai",
    name: "Dubai",
    display: "Dubai",
    region: "UAE",
    country: "United Arab Emirates",
    coordinates: "25.2048°N 55.2708°E",
    localProof:
      "1.5 hours behind Delhi — full working-hour overlap. We regularly ship for DIFC and JLT teams.",
    localSignal: "full working-hour overlap with DIFC and JLT",
    timezone: "GST (UTC+4)",
  },
];

export const INDUSTRIES = [
  {
    slug: "saas",
    name: "SaaS",
    display: "SaaS",
    challenge:
      "Most SaaS products stall because churn compounds faster than acquisition. The fix is usually upstream: onboarding, activation, and expansion revenue — not more paid traffic.",
    approach:
      "We rebuild activation flows, instrument product analytics (PostHog, Mixpanel), and ship features proven to lift expansion MRR.",
    regulations: "SOC2, GDPR, CCPA",
    metric: "22x user growth on a rebuilt multi-tenant core (Pulse SaaS)",
  },
  {
    slug: "fintech",
    name: "Fintech",
    display: "Fintech",
    challenge:
      "Fintech products live or die on trust and speed. Every unnecessary loading spinner is a conversion leak; every compliance gap is a business-ending risk.",
    approach:
      "We build with bank-grade security posture (encrypted at rest + in flight, biometric signing, audit logs) and millisecond UX polish — the standard SOC2/RBI expects.",
    regulations: "RBI, PCI-DSS, SOC2, AML/KYC",
    metric: "40ms swap execution, 4.9 App Store rating (Nova Fintech)",
  },
  {
    slug: "healthcare",
    name: "Healthcare",
    display: "Healthcare",
    challenge:
      "Healthcare tech fails when it adds to clinician workload. The winning systems give time back — decision support, automated documentation, fast retrieval.",
    approach:
      "We build HIPAA/DISHA-ready systems with RAG-powered clinical assistants, offline-capable mobile, and audit-grade data handling.",
    regulations: "HIPAA, DISHA (India), HL7/FHIR compatible",
    metric: "85% less time searching across 40K+ clinicians (Quantum Health AI)",
  },
  {
    slug: "e-commerce",
    name: "E-commerce",
    display: "E-commerce",
    challenge:
      "D2C and marketplace growth has gotten harder — paid CAC is up, Meta attribution is broken, and product pages all look the same. The winners own content, search, and retention.",
    approach:
      "Programmatic SEO for category pages, AI-powered recommendations, lifecycle email/SMS, and checkout optimisation that actually moves conversion.",
    regulations: "PCI-DSS, GDPR if EU, Consumer Protection Act",
    metric: "3.8x organic traffic from programmatic SEO (InsureX-style build)",
  },
  {
    slug: "edtech",
    name: "EdTech",
    display: "EdTech",
    challenge:
      "EdTech apps that retain learners solve two problems: fighting the 30-day cliff, and matching content difficulty to individual pace. Most don't.",
    approach:
      "We build adaptive learning systems with spaced repetition, progress analytics, and lightweight social features that actually lift completion rates.",
    regulations: "COPPA (if under 13), FERPA (US), GDPR",
    metric: "Offline-first mobile architecture used in low-bandwidth markets",
  },
  {
    slug: "insurance",
    name: "Insurance",
    display: "Insurance",
    challenge:
      "Insurance CAC is brutal because products are opaque and comparison sites dominate search. Winners own the trust layer — education, transparency, speed to quote.",
    approach:
      "Conversion-optimised quote funnels, partner-API integration with caching, and AI-generated plain-English explainers for regulated products.",
    regulations: "IRDAI (India), Solvency II (EU)",
    metric: "10k+ quotes/month within first quarter (InsureX Capital)",
  },
  {
    slug: "real-estate",
    name: "Real Estate",
    display: "Real Estate",
    challenge:
      "Real estate tech that actually sells needs rich media performance, geo-filtering, and genuine trust signals — not another generic listing site.",
    approach:
      "High-performance image pipelines, map-based search with clustering, verified-seller workflows, and SEO-ready property pages at scale.",
    regulations: "RERA (India), Fair Housing (US)",
    metric: "Programmatic property pages indexed at scale",
  },
  {
    slug: "logistics",
    name: "Logistics",
    display: "Logistics",
    challenge:
      "Logistics software lives in the gap between field operations (drivers, warehouses) and office dashboards. The hard part is making the field app feel invisible.",
    approach:
      "Offline-first mobile, real-time tracking with efficient backend pub-sub, and dashboards that surface anomalies instead of data.",
    regulations: "GDPR for driver PII, FMCSA (US), RTO compliance (India)",
    metric: "Real-time fleet visibility at enterprise scale",
  },
];

// Derived: every combination, flattened with a precomputed slug.
export const CITY_PAGES = Object.entries(SERVICE_SEO).flatMap(([sKey, s]) =>
  CITIES.map((c) => ({
    kind: "city",
    slug: `${s.slug}-in-${c.slug}`,
    service: { key: sKey, ...s },
    city: c,
  })),
);

export const INDUSTRY_PAGES = Object.entries(SERVICE_SEO).flatMap(([sKey, s]) =>
  INDUSTRIES.map((i) => ({
    kind: "industry",
    slug: `${s.slug}-for-${i.slug}`,
    service: { key: sKey, ...s },
    industry: i,
  })),
);

export const ALL_PROG_PAGES = [...CITY_PAGES, ...INDUSTRY_PAGES];

export const findProgPage = (slug) => ALL_PROG_PAGES.find((p) => p.slug === slug);
